import React, { useState } from 'react';
import OrgConnectionSettingsDialog from '@/components/organisation/OrgConnectionSettingsDialog';
import type { MerchantConnectionsApi } from '@/hooks/useMerchantConnections';
import { useNow } from '@/hooks/useNow';
import { ConnectionsOwner, FeedConnection, connectorIdOf, isPlaidConnectionRecord } from '@/shared/merchantFeeds';
import { FeedRow } from './FeedRows';

// A list of connection rows sharing one Connection Settings dialog. Used by
// Data Feeds (FIG-321) and Licensed Seller Monitoring (FIG-323).

interface FeedListProps {
  ariaLabel: string;
  feeds: FeedConnection[];
  detailFor: (feed: FeedConnection) => string;
  owner: ConnectionsOwner;
  connections: MerchantConnectionsApi;
  canEdit: boolean;
  /** Shown when there are no feeds and no extra rows. */
  emptyText?: string;
  /** Extra rows after the feeds, e.g. CSV upload summaries. */
  children?: React.ReactNode;
}

export const FeedList: React.FC<FeedListProps> = ({
  ariaLabel,
  feeds,
  detailFor,
  owner,
  connections,
  canEdit,
  emptyText,
  children,
}) => {
  const [settingsFor, setSettingsFor] = useState<FeedConnection | null>(null);
  // One clock per list keeps "Last sync … ago" current while Settings stays open (Greptile P2 on #287).
  const now = useNow(60_000, feeds.length > 0);
  return (
    <>
      <ul className="space-y-2" aria-label={ariaLabel}>
        {feeds.map((feed) => (
          <FeedRow
            key={feed.key}
            feed={feed}
            detail={detailFor(feed)}
            status={connections.getStatusIndicator(feed.value, connectorIdOf(feed.key, feed.value), false)}
            lastSync={connections.getLastSync(connectorIdOf(feed.key, feed.value))}
            now={now}
            canEdit={canEdit}
            inherited={connections.isInheritedConnection(feed.key)}
            removing={connections.removingId === feed.key}
            locked={connections.busy}
            isFivetran={!isPlaidConnectionRecord(feed.value)}
            onOpenSettings={() => setSettingsFor(feed)}
            onRemove={() => connections.deleteConnection(feed.key, feed.value)}
          />
        ))}
        {children}
        {feeds.length === 0 && !children && emptyText && (
          <li className="rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground">{emptyText}</li>
        )}
      </ul>
      {settingsFor && (
        <OrgConnectionSettingsDialog
          open={!!settingsFor}
          onOpenChange={(open) => !open && setSettingsFor(null)}
          connectorId={connectorIdOf(settingsFor.key, settingsFor.value)}
          connectionName={settingsFor.name}
          service={settingsFor.value.service || settingsFor.value.type || ''}
          orgId={owner.id}
          orgConnections={owner.connections}
        />
      )}
    </>
  );
};

export default FeedList;
